import { and, eq, isNull } from 'drizzle-orm';
import { db, runInTransaction } from '@/db/client';
import { goals } from '@/db/schema';
import { deleteGoal } from '@/db/repositories/goalRepository';
import {
    deleteReservaMovement,
    getRawAvailableByAccount,
    getReservaById,
    guardar,
    insertReserva,
    registrarRendimento,
    unarchiveReserva,
    type CreateReservaInput,
    type Reserva,
} from '@/db/repositories/reservaRepository';
import {
    creditAccountBalanceAdjustment,
    creditAccountYield,
    removeReservaYieldIncome,
} from '@/db/repositories/transactionRepository';
import { ExpectedError } from '@/lib/errorUtils';
import { formatCurrency } from '@/lib/formatting';
import i18n from '@/lib/i18n';

// Fluxos que mexem na CONTA e na CAIXINHA juntas. O reservaRepository segue puro (ADR 0015):
// não escreve em transações. Quem credita/apaga a transação é este módulo, chamado pelos hooks.
//
// Aportes: completam a conta com um Ajuste de saldo e depois reservam.
// Guardar: a falta é o que o Disponível LIVRE não cobre. Com o Disponível negativo (conta no
// vermelho ou caixinha submersa, ADR 0034), entra só o valor pedido, ele fica todo guardado e o
// Disponível continua negativo — cobrir o negativo creditava dinheiro que não entrou (ADR 0167).
// Reativar segue medindo no Disponível BRUTO: o unarchive exige o congelado inteiro livre.

function shortfallOver(raw: number, amount: number): number {
    return Math.max(0, Math.round((amount - raw) * 100) / 100);
}

/**
 * "Completar e reservar" / "Adicionar à conta e reservar" (ADR 0017, 0167). Leitura fresca e
 * crédito RELATIVO (nunca set absoluto a partir de cache). Não é uma transação única: se o
 * guardar falhar depois do crédito, o dinheiro entrou de verdade. Com o Disponível ≥ 0 um retry
 * recalcula a falta sobre o Disponível já maior e não credita de novo; com ele negativo, o retry
 * credita outra vez. Devolve o valor guardado.
 */
export async function guardarComAporte(input: {
    reservaId: string;
    accountId: string;
    amount: number;
    date: string;
    notes?: string;
}): Promise<number> {
    const amt = Math.round(input.amount * 100) / 100;
    if (amt <= 0) return 0;
    const raw = await getRawAvailableByAccount(input.accountId);
    const shortfall = shortfallOver(Math.max(0, raw), amt);
    if (shortfall > 0) await creditAccountBalanceAdjustment(input.accountId, shortfall);
    return guardar(input.reservaId, amt, input.date, input.notes, { incoming: shortfall });
}

/**
 * "Dinheiro novo" (ADR 0160): o valor entra na conta como Ajuste de saldo e fica todo guardado.
 * Credita só o valor — um Disponível negativo continua negativo (ADR 0167). Devolve o guardado.
 */
export async function guardarDinheiroNovo(input: {
    reservaId: string;
    accountId: string;
    amount: number;
    date: string;
    notes?: string;
}): Promise<number> {
    const amt = Math.round(input.amount * 100) / 100;
    if (amt <= 0) return 0;
    await creditAccountBalanceAdjustment(input.accountId, amt);
    return guardar(input.reservaId, amt, input.date, input.notes, { incoming: amt });
}

/** "Adicionar saldo e reativar" (ADR 0019): completa a conta e reativa a caixinha cheia. */
export async function reativarComAporte(input: { id: string; accountId: string; frozen: number }): Promise<void> {
    const amt = Math.round(input.frozen * 100) / 100;
    if (amt > 0) {
        const shortfall = shortfallOver(await getRawAvailableByAccount(input.accountId), amt);
        if (shortfall > 0) await creditAccountBalanceAdjustment(input.accountId, shortfall);
    }
    await unarchiveReserva(input.id);
}

/**
 * Rendimento (ADR 0161): credita a RECEITA na conta ("Rendimento · <caixinha>", Investimentos ·
 * Juros / Rendimentos) e registra o movimento de rendimento ligado a ela, somando no reservado.
 * O Disponível não muda. Caixinha excluída, arquivada ou com a conta fora não recebe nada: a
 * receita nasceria sem elo ativo, solta na conta. Se ela sumir entre esta leitura e o movimento, a
 * receita recém-criada sai de novo. Devolve o valor registrado (0 = nada).
 */
export async function registrarRendimentoComReceita(input: {
    reservaId: string;
    accountId: string;
    amount: number;
    date: string;
    notes?: string;
}): Promise<number> {
    const amt = Math.round(input.amount * 100) / 100;
    if (amt <= 0) return 0;
    const reserva = await getReservaById(input.reservaId);
    if (!reserva || reserva.deletedAt || reserva.isArchived === 1 || reserva.accountInactive === 1) return 0;
    const income = await creditAccountYield(
        input.accountId,
        amt,
        input.date,
        `Rendimento · ${reserva.name}`,
        input.notes,
    );
    if (!income) return 0;
    const moved = await registrarRendimento(input.reservaId, amt, input.date, input.notes, income.id);
    if (moved <= 0) await removeReservaYieldIncome(income.id);
    return moved;
}

/**
 * Excluir um movimento pelo histórico da caixinha. Rendimento ligado leva a receita junto (ADR
 * 0161): primeiro o movimento (que pode ser bloqueado, ex.: o valor já foi resgatado), depois a
 * receita — assim um bloqueio nunca deixa a receita apagada com o rendimento ainda na caixinha.
 */
export async function excluirMovimentoDaCaixinha(movementId: string): Promise<void> {
    const linkedIncomeId = await deleteReservaMovement(movementId);
    if (linkedIncomeId) await removeReservaYieldIncome(linkedIncomeId);
}

/**
 * Meta vira caixinha (ADR 0162), numa transação só: tudo ou nada.
 *
 * - A meta precisa existir (um link com id velho não cria nada nem pula o limite do plano).
 * - `apart` ("é um valor à parte") credita a conta antes, com o déficit, como na criação.
 * - Se o Disponível não cobre o valor inicial pedido, nada muda. Antes, a meta saía mesmo com a
 *   caixinha nascendo com menos: R$ 2.000 de meta numa conta com R$ 300 livres perdiam R$ 1.700
 *   de histórico, sem desfazer (revisão de 28/09).
 */
export async function transformarMetaEmCaixinha(input: {
    goalId: string;
    reserva: CreateReservaInput;
    apart: boolean;
}): Promise<Reserva> {
    const requested = Math.round((input.reserva.initialAmount ?? 0) * 100) / 100;
    return runInTransaction(async () => {
        const [goal] = await db.select({ id: goals.id }).from(goals)
            .where(and(eq(goals.id, input.goalId), isNull(goals.deletedAt)));
        if (!goal) throw new ExpectedError(i18n.t('goals:form.convertGoalMissing'));

        if (input.apart && requested > 0) {
            const raw = await getRawAvailableByAccount(input.reserva.accountId);
            await creditAccountBalanceAdjustment(input.reserva.accountId, requested + Math.max(0, -raw));
        }
        const created = await insertReserva(input.reserva);
        if (created.currentAmount + 0.001 < requested) {
            throw new ExpectedError(i18n.t('goals:form.convertShortfall', {
                available: formatCurrency(created.currentAmount),
            }));
        }
        await deleteGoal(input.goalId);
        return created;
    });
}
